import { axiosDelete, axiosGet, axiosPatch, axiosPost } from '@/api/handlers';
import { useMutation, useQuery } from '@/api/hooks';
import { useInvalidateQuery } from '@/hooks';
import { z } from 'zod';
import { queryKeyMap } from '../../../../_utils/query-keys';
import {
  PaymentEdgeMutationParamsSchema,
  PaymentEdgesModel,
} from '../../../_models/payment-edge';

export const usePaymentEdges = (projectId: string) => {
  return useQuery({
    queryKey: queryKeyMap.paymentEdges(projectId),
    queryFn: async () => {
      const paymentEdges = await axiosGet(
        `/api/projects/${projectId}/payment-edges/`,
      );
      return PaymentEdgesModel.parse(paymentEdges);
    },
  });
};

export const useCreatePaymentEdge = () => {
  const { invalidate } = useInvalidateQuery();
  return useMutation({
    mutationFn: async (
      values: z.infer<typeof PaymentEdgeMutationParamsSchema>,
    ) => {
      const { projectId, ...payload } =
        PaymentEdgeMutationParamsSchema.parse(values);
      const res = await axiosPost(
        `/api/projects/${projectId}/payment-edges/`,
        payload,
      );
      await Promise.all([
        invalidate(queryKeyMap.paymentGroups(projectId)),
        invalidate(queryKeyMap.paymentEdges(projectId)),
        invalidate(queryKeyMap.paymentGraphData(projectId)),
      ]);
      return res;
    },
  });
};

export const useUpdatePaymentEdge = () => {
  const { invalidate } = useInvalidateQuery();
  return useMutation({
    mutationFn: async (
      values: z.infer<typeof PaymentEdgeMutationParamsSchema> & {
        id: string;
      },
    ) => {
      const { id, projectId, ...payload } =
        PaymentEdgeMutationParamsSchema.extend({
          id: z.string().uuid(),
        }).parse(values);
      const res = await axiosPatch(
        `/api/projects/${projectId}/payment-edges/${id}/`,
        payload,
      );
      await Promise.all([
        invalidate(queryKeyMap.paymentGroups(projectId)),
        invalidate(queryKeyMap.paymentEdges(projectId)),
        invalidate(queryKeyMap.paymentGraphData(projectId)),
      ]);
      return res;
    },
  });
};

export const useDeletePaymentEdge = () => {
  const { invalidate } = useInvalidateQuery();
  return useMutation({
    mutationFn: async ({
      id,
      projectId,
    }: {
      id: string;
      projectId: string;
    }) => {
      const res = await axiosDelete(
        `/api/projects/${projectId}/payment-edges/${id}/`,
      );
      await Promise.all([
        invalidate(queryKeyMap.paymentGroups(projectId)),
        invalidate(queryKeyMap.paymentEdges(projectId)),
        invalidate(queryKeyMap.paymentGraphData(projectId)),
      ]);
      return res;
    },
  });
};
